import React, { useState, useEffect } from 'react';
import Button from '../../components/Button';
import AddInvoiceModal from '../../components/AddInvoiceModal';
import enhancedApiClient from '../../api/enhancedApiClient';

const VendorPayments = () => {
  const [filter, setFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [searchQuery, setSearchQuery] = useState('');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceType, setInvoiceType] = useState('invoice');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [vendorEvents, setVendorEvents] = useState([]);

  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
        const profile = await enhancedApiClient.getUser(user.username);
        setUserProfile(profile);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);

  const fetchPayments = async () => {
    try {
      if (currentUser && userProfile?.currentVendorId) {
        // Fetch all vendor events
        const eventsResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${userProfile.currentVendorId}/events`);
        const events = eventsResponse.ok ? await eventsResponse.json() : [];
        setVendorEvents(events);

        // Fetch invoices from all events
        const allPayments = [];
        for (const event of events) {
          const invoices = await enhancedApiClient.getInvoices(event.id);
          const vendorInvoices = invoices
            .filter(inv => inv.vendorId === userProfile.currentVendorId)
            .map(inv => ({ ...inv, eventName: event.name }));
          allPayments.push(...vendorInvoices);
        }
        setPayments(allPayments);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, [currentUser, userProfile]);

  const getFilteredPayments = () => {
    let filtered = payments;

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => {
        const name = p.name || `Invoice #${p.id?.split('-')[1] || ''}`;
        return name.toLowerCase().includes(q) || (p.eventName || '').toLowerCase().includes(q);
      });
    }

    // Type filter
    if (typeFilter === 'Invoices') filtered = filtered.filter(p => p.invoiceType !== 'quote');
    else if (typeFilter === 'Quotes') filtered = filtered.filter(p => p.invoiceType === 'quote');

    // Status filter
    if (filter === 'Paid') filtered = filtered.filter(p => p.status === 'paid');
    else if (filter === 'Overdue') filtered = filtered.filter(p => p.dueDate && new Date(p.dueDate) < new Date() && p.status !== 'paid');
    else if (filter === 'Upcoming') filtered = filtered.filter(p => (!p.dueDate || new Date(p.dueDate) >= new Date()) && p.status !== 'paid');

    return filtered.sort((a, b) => {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate) - new Date(b.dueDate);
    });
  };

  const filteredPayments = getFilteredPayments();

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #E5E7EB', borderTop: '3px solid #6B9080', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>Loading payments...</div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ backgroundColor: '#E8F4E8', padding: '48px 24px 80px 24px', textAlign: 'center', marginBottom: '60px' }}>
        <h1 className="page-title" style={{ fontSize: '56px', fontWeight: '400', color: '#1F2937', margin: 0, fontFamily: 'Georgia, serif', letterSpacing: '-0.02em' }}>
          Payments
        </h1>
      </div>

      {/* Main Content */}
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        {/* Search and Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search invoices & quotes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', backgroundColor: '#F9FAFB' }}
          />

          <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', backgroundColor: 'white', minWidth: '120px' }}>
            <option>All</option>
            <option>Upcoming</option>
            <option>Paid</option>
            <option>Overdue</option>
          </select>

          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', backgroundColor: 'white', minWidth: '120px' }}>
            <option>All Types</option>
            <option>Invoices</option>
            <option>Quotes</option>
          </select>

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="primary" onClick={() => { setInvoiceType('invoice'); setShowInvoiceModal(true); }}>Add Invoice</Button>
            <Button variant="outline" onClick={() => { setInvoiceType('quote'); setShowInvoiceModal(true); }}>Add Quote</Button>
          </div>
        </div>

        {/* Payments List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredPayments.length === 0 ? (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '40px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center', color: '#6B7280' }}>
              No payments found
            </div>
          ) : (
            filteredPayments.map(payment => (
              <div
                key={payment.id}
                onClick={() => setSelectedPayment(payment)}
                style={{
                  backgroundColor: 'white', borderRadius: '12px', padding: '20px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center',
                  gap: '16px', cursor: 'pointer', transition: 'box-shadow 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
              >
                <div style={{
                  width: '48px', height: '48px', backgroundColor: '#E8F4E8', borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '600', fontSize: '18px', color: '#1F2937' }}>
                      {payment.name || `Invoice #${payment.id?.split('-')[1] || 'Unknown'}`}
                    </span>
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                      backgroundColor: payment.invoiceType === 'quote' ? '#FEF3C7' : '#E0F2FE',
                      color: payment.invoiceType === 'quote' ? '#92400E' : '#0369A1'
                    }}>
                      {payment.invoiceType === 'quote' ? 'Quote' : 'Invoice'}
                    </span>
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '14px' }}>
                    {payment.eventName || 'Unknown Event'} · {payment.status || 'Pending'}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {(payment.pdfUrl || payment.documentUrl) && (
                    <Button variant="outline" size="small" onClick={(e) => {
                      e.stopPropagation();
                      window.open(payment.pdfUrl || payment.documentUrl, '_blank');
                    }}>View Doc</Button>
                  )}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '600', fontSize: '18px', color: '#1F2937', marginBottom: '4px' }}>
                      ${payment.depositAmount || payment.total || 0}
                    </div>
                    <div style={{ color: '#6B7280', fontSize: '14px' }}>
                      {payment.dueDate ? `Due ${new Date(payment.dueDate).toLocaleDateString()}` : 'No due date'}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Invoice Detail Modal */}
        {selectedPayment && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto'
            }}>
              <div style={{ backgroundColor: '#E8F4E8', padding: '24px', textAlign: 'center', position: 'relative', borderRadius: '12px 12px 0 0' }}>
                <button onClick={() => setSelectedPayment(null)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6B7280' }}>×</button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <h2 style={{ fontSize: '28px', fontWeight: '400', color: '#1F2937', margin: 0, fontFamily: 'Georgia, serif' }}>
                    {selectedPayment.name || 'Invoice Details'}
                  </h2>
                  <span style={{
                    padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                    backgroundColor: selectedPayment.invoiceType === 'quote' ? '#FEF3C7' : '#E0F2FE',
                    color: selectedPayment.invoiceType === 'quote' ? '#92400E' : '#0369A1'
                  }}>
                    {selectedPayment.invoiceType === 'quote' ? 'Quote' : 'Invoice'}
                  </span>
                </div>
              </div>

              <div style={{ padding: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Event</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#1F2937' }}>{selectedPayment.eventName || 'N/A'}</div>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Status</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#1F2937', textTransform: 'capitalize' }}>{selectedPayment.status || 'Pending'}</div>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Amount</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#1F2937' }}>${selectedPayment.depositAmount || selectedPayment.total || 0}</div>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Due Date</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#1F2937' }}>{selectedPayment.dueDate ? new Date(selectedPayment.dueDate).toLocaleDateString() : 'No due date'}</div>
                  </div>
                </div>

                {selectedPayment.description && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Description</div>
                    <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.5' }}>{selectedPayment.description}</p>
                  </div>
                )}

                {selectedPayment.notes && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Notes</div>
                    <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.5' }}>{selectedPayment.notes}</p>
                  </div>
                )}

                {selectedPayment.createdAt && (
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '16px' }}>
                    Created {new Date(selectedPayment.createdAt).toLocaleDateString()}
                  </div>
                )}

                {(selectedPayment.pdfUrl || selectedPayment.documentUrl) && (
                  <button
                    onClick={() => window.open(selectedPayment.pdfUrl || selectedPayment.documentUrl, '_blank')}
                    style={{
                      width: '100%', padding: '12px', backgroundColor: '#E8F4E8', color: '#6B9080',
                      border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                    }}
                  >View Document</button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Invoice/Quote Modal */}
        <AddInvoiceModal
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          type={invoiceType}
          isVendor={true}
          vendorEvents={vendorEvents}
          onSubmit={async (data) => {
            try {
              let documentUrl = null;
              if (data.pdfFile) {
                const uploadResult = await enhancedApiClient.uploadFile(data.pdfFile);
                documentUrl = uploadResult.fileUrl;
              }

              const invoice = {
                eventId: data.eventId || data.selectedEventId,
                invoiceType: invoiceType,
                name: data.name,
                description: data.description,
                notes: data.notes,
                vendorId: userProfile?.currentVendorId,
                depositAmount: data.depositAmount ? parseFloat(data.depositAmount) : 0,
                lineItems: data.lineItems,
                dueDate: data.dueDate,
                documentUrl,
                pdfUrl: documentUrl,
                status: 'pending',
                createdAt: new Date().toISOString()
              };

              await enhancedApiClient.createInvoice(invoice);
              setShowInvoiceModal(false);
              await fetchPayments();
            } catch (error) {
              console.error('Error creating invoice:', error);
              alert('Failed to create. Please try again.');
            }
          }}
        />
      </div>
    </div>
  );
};

export default VendorPayments;
